// TGCLOUD-1669 Phase 2 — what the pinned person header says, and in what order.
//
// Misidentifying a patient is the clinical safety risk in this application, and
// the header is the only thing tying a scrolling panel of forms to a person. So
// it is pinned (it lives outside ScrollableContent and stays there), and it
// carries enough to CONFIRM identity rather than merely name it.
//
// ---------------------------------------------------------------------------
// THE RANK ORDER, and why the cut falls where it does
// ---------------------------------------------------------------------------
// A fact earns a place if a clinician uses it to confirm they have the right
// person, or if it changes how everything below it is read. Ranked:
//
//   1  name, or the member id when there is none        (the headline)
//   2  member id, family id, UPN, proband marker        (the id line)
//   3  lock state                                       (owned by the sidebar,
//                                                        which holds the hook)
//   4  sex, age, date of birth
//   5  ethnicity
//   6  the tenant-labelled hospital number
//   7  healthcare / NHS number
//   8  mobile, email
//   9  person tags
//
// Facts are dropped from the BOTTOM of that list upwards, so the hospital
// number goes before the ethnicity does. What never drops at any width is the
// identity itself: name, member id and lock state.
//
// This module returns every rankable fact and lets the component decide how
// many fit. `resolveSidebarHeader` in ./util delegates here and keeps its old
// shape, so its existing callers and tests are untouched.

import {
  formatPartialDate,
  partialDateFromBackend
} from 'src/util/partialDate';
import { calculateAgePartial, formatAgeDisplay } from 'src/util/util';
import { DEFAULT_PEDIGREE_FIELD_LABELS } from 'src/hooks/usePedigreeFieldLabels';
import { resolveSidebarHeader } from './util';

// Direct identifiers. On an anonymous deployment these are omitted from the
// header entirely rather than rendered as "withheld".
//
// Two reasons for omitting rather than showing a placeholder. The compact
// header shows about five facts at 400px, and spending them on six "withheld"
// rows pushes out sex, age and ethnicity, which are exactly what remains useful
// on an anonymous deployment. And the PERSON section directly beneath already
// renders these fields with a withheld notice, so nothing is hidden from the
// user, only from the summary line.
//
// `dob`/`dod` are on PII_LABEL_KEYS. The rest match the fields PersonMenu
// already hides in anonymous mode, so the header agrees with the form under it
// rather than contradicting it.
const WITHHELD_WHEN_ANONYMOUS = Object.freeze([
  'dob',
  'dod',
  'hospitalNumber',
  'healthcareId',
  'mobile',
  'email'
]);

const titleCase = (s) =>
  typeof s === 'string' && s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

const clean = (v) =>
  typeof v === 'string' && v.trim() ? v.trim() : v == null ? null : v || null;

/**
 * @param {object} args
 * @param {object|null} args.data       selected node, merged with its memberRecords row
 * @param {object|null} args.selectedUser
 * @param {boolean} args.anonymous      the FAIL-CLOSED flag (isAnonymousOrLoading)
 * @param {object} args.labels          tenant field labels
 * @param {string|null} args.personLinkStatus
 */
export function buildPersonHeader({
  data,
  selectedUser,
  anonymous = false,
  labels = DEFAULT_PEDIGREE_FIELD_LABELS,
  personLinkStatus = null
} = {}) {
  const memberId = data?.proBandId || data?.familyMemberId;

  // The headline, the overline and the id beneath come from the existing
  // resolveSidebarHeader rather than being reimplemented here. Its three
  // fallbacks (anonymous, de-identified, no name recorded) are subtle, already
  // pinned by sidebarOrder.test.jsx, and were fixed only last week for
  // TGCLOUD-1768. This function EXTENDS that result; it does not replace it.
  const base = resolveSidebarHeader({
    data,
    selectedUser,
    anonymousActive: anonymous
  });

  // Family view. It names nobody on purpose: a surname is not reliably a
  // family's identity, and it is PII, so it would vanish on an anonymous
  // deployment where the G number must not.
  if (!memberId) {
    return {
      ...base,
      kind: 'family',
      subtitle: null,
      idParts: [],
      facts: [],
      tags: [],
      proband: false,
      deceased: false,
      secondaryRecord: false,
      isIdentifierPrimary: true
    };
  }

  // `secondary` is null whenever the headline is already the id, so there is a
  // spare line in exactly that case. Saying where the person sits in the family
  // is more use there than repeating the id.
  const isIdentifierPrimary = base.secondary === null;
  const familyId = data?.familyId || selectedUser?.familyId || '';
  // With no name the id is already the headline, so this line says where the
  // person sits instead of repeating it. A missing UPN is left out rather than
  // rendered as `UPN ?`: a question mark tells a clinician nothing they could
  // act on, and reads as an error in a field that is simply not recorded.
  const upnKnown =
    data?.upn !== undefined && data?.upn !== null && data?.upn !== '';
  const subtitle = isIdentifierPrimary
    ? [upnKnown ? `UPN ${data.upn}` : null, familyId ? `of ${familyId}` : null]
        .filter(Boolean)
        .join(' ') || null
    : base.secondary;

  const deceased = data?.isAlive === 'false' || data?.isAlive === false;

  const dobPartial = partialDateFromBackend({
    iso: data?.dob,
    estimatedFlag: data?.estimatedDob,
    precisionField: data?.dobPrecision
  });
  const dodPartial = partialDateFromBackend({
    iso: data?.dod,
    estimatedFlag: data?.estimatedDod,
    precisionField: data?.dodPrecision
  });

  const ageResult = dobPartial
    ? calculateAgePartial(dobPartial, deceased ? dodPartial : null)
    : null;
  const ageText = ageResult
    ? formatAgeDisplay(ageResult)
    : data?.age != null && data?.age !== ''
    ? String(data.age)
    : null;

  const precisionNote = (partial) =>
    !partial
      ? null
      : partial.precision === 'year'
      ? 'year only'
      : partial.precision === 'month'
      ? 'month and year only'
      : null;

  const ethnicity = Array.isArray(data?.ethnicity)
    ? data.ethnicity.filter(Boolean)
    : [];

  // Rank order 4 to 8. Nulls are kept in the list with a null value so a caller
  // can choose to render "not recorded" rather than silently reordering the
  // run; the sidebar drops them.
  const ranked = [
    {
      key: 'sex',
      label: 'Sex',
      value: clean(titleCase(data?.sab || data?.gender))
    },
    {
      key: 'age',
      label: deceased ? 'Age at death' : 'Age',
      value: ageText,
      // A range or a tilde already says it, but the word is what a clinician
      // can read aloud.
      note: ageResult && !ageResult.isExact ? 'approximate' : null
    },
    {
      key: 'dob',
      label: 'Date of birth',
      value: dobPartial ? formatPartialDate(dobPartial) : null,
      note: precisionNote(dobPartial)
    },
    deceased
      ? {
          key: 'dod',
          label: 'Date of death',
          value: dodPartial ? formatPartialDate(dodPartial) : null,
          note: precisionNote(dodPartial)
        }
      : null,
    {
      key: 'ethnicity',
      label: 'Ethnicity',
      value: ethnicity.length ? ethnicity.join(', ') : null
    },
    {
      key: 'hospitalNumber',
      label: labels.hospitalNumber,
      value: clean(data?.hospitalNumber)
    },
    {
      key: 'healthcareId',
      label: labels.healthcareId,
      value: clean(data?.healthcareId)
    },
    { key: 'mobile', label: labels.mobile, value: clean(data?.mobile) },
    { key: 'email', label: 'Email', value: clean(data?.email) }
  ].filter(Boolean);

  const facts = ranked.filter(
    (f) => !(anonymous && WITHHELD_WHEN_ANONYMOUS.includes(f.key))
  );

  const tags = Array.isArray(data?.personTags)
    ? data.personTags.filter((t) => t && t.tag)
    : [];

  return {
    ...base,
    kind: 'person',
    subtitle,
    isIdentifierPrimary,
    idParts: [
      { key: 'familyId', label: 'Family', value: familyId || null },
      {
        key: 'upn',
        label: 'UPN',
        value: data?.upn != null ? String(data.upn) : null
      }
    ].filter((p) => p.value),
    facts,
    tags,
    proband: Boolean(data?.proBandId),
    deceased,
    secondaryRecord: personLinkStatus === 'secondary'
  };
}

export default buildPersonHeader;
